import {Box, Text} from '@mantine/core';
import Image from 'next/image';
import {useRouter} from 'next/router';
import type {FC} from 'react';
import type {GetSkillCheckLastResultType} from '../../../../../core/entities/skillCheck/schema';
import {useIsAppWebView} from '../../../../../shared/hooks/useIsAppWebView';
import {getIsToday} from '../../usecase/getIsToday';
import {getLogoImage} from '../../usecase/getLogoImage';

type CardActionButtonProps = {
  text: string;
  path: string;
  className: string;
  disabled?: boolean;
  handleNavigation: (path: string) => void;
};

type CategoryCardProps = {
  id: number;
  name: string;
  genre: string;
  lastResult?: GetSkillCheckLastResultType;
};

const CardActionButton: FC<CardActionButtonProps> = ({
  text,
  path,
  className,
  disabled = false,
  handleNavigation,
}) => (
  <button
    type="button"
    disabled={disabled}
    className={`${className} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    onClick={() => {
      handleNavigation(path);
    }}
  >
    <Text className="text-base font-bold">{text}</Text>
  </button>
);

export const CategoryCard: FC<CategoryCardProps> = ({
  id,
  name,
  genre,
  lastResult,
}) => {
  const isWebView = useIsAppWebView();
  const router = useRouter();
  const src = getLogoImage(name);

  const handleNavigation = (path: string) => {
    if (isWebView) {
      location.href = path;
    } else {
      void router.push(path);
    }
  };

  return (
    <Box className="flex flex-col w-[353px] h-[312px] rounded-12">
      {src && (
        <Image src={src} className="object-contain rounded-t-12" alt={name} />
      )}
      <Box className="flex flex-col flex-1 py-16 px-20 justify-between items-center bg-white rounded-b-12">
        <Box className="w-full">
          <Text className="text-base text-black-1">{genre}</Text>
          <Text className="text-xl">{name}</Text>

          {lastResult && (
            <Box className="flex items-center justify-between">
              {getIsToday(lastResult.testDateTime) ? (
                <Text className="text-base text-primary-1 font-bold">
                  最新のスコア：{lastResult.userScore} / {lastResult.maxScore}点
                </Text>
              ) : (
                <Text className="text-base text-black-3">
                  前回のスコア：{lastResult.userScore}点
                </Text>
              )}
              <Text className="text-base text-black-3">
                上位{lastResult.topPercentage.toFixed(1)}%
              </Text>
            </Box>
          )}
        </Box>
        <Box className="flex flex-col h-[90px] justify-start gap-8">
          {getIsToday(lastResult?.testDateTime) ? (
            <CardActionButton
              text="本日はチェック済み"
              path=""
              className="bg-black-5 text-black-1 w-[200px] h-40 rounded-[20px] "
              disabled
              handleNavigation={handleNavigation}
            />
          ) : (
            <CardActionButton
              text="チェックする"
              path={`/engineer/skill_check/question/${id}`}
              className="bg-accent-1 text-white w-[200px] h-40 rounded-[20px]"
              handleNavigation={handleNavigation}
            />
          )}
          {lastResult && (
            <CardActionButton
              text="前回の結果を見る"
              path={`/engineer/skill_check/result/${lastResult?.historyUUID}`}
              className="bg-primary text-white w-[200px] h-40 rounded-[20px]"
              handleNavigation={handleNavigation}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
